/**
 * Jobs, shifts, crew allocations and job documents.
 * Office side (directors and admins): Supabase under RLS, plus the API for files and crew logins.
 * Crew side (controllers): the API's /crew routes, which only ever return their own shifts.
 */
import { adminBlob, adminFetch } from "./api";
import { crewBlob, crewFetch } from "../modules/crew-api";
import { canConfirm } from "./agreements-data";
import { localInstant, shiftTypeFor } from "./format";
import { PREVIEW } from "./preview";
import {
  supabase,
  type CrewMember,
  type DocumentAck,
  type JobDetail,
  type JobFields,
  type JobPack,
  type JobSummary,
  type MyShift,
} from "./supabase";

const previewOnly = (): never => {
  throw new Error("Jobs are not available in preview mode.");
};

let orgPromise: Promise<string> | null = null;
/** The signed-in user's organisation (needed on inserts; RLS checks it). */
async function organisationId(): Promise<string> {
  orgPromise ??= (async () => {
    const { data: s } = await supabase.auth.getSession();
    const uid = s.session?.user.id;
    if (!uid) throw new Error("Sign in again.");
    const { data, error } = await supabase.from("profiles").select("organisation_id").eq("id", uid).single();
    if (error) throw error;
    return data.organisation_id as string;
  })();
  try {
    return await orgPromise;
  } catch (e) {
    orgPromise = null;
    throw e;
  }
}

/* ---------- Jobs (office) ---------- */

export async function listJobs(): Promise<JobSummary[]> {
  if (PREVIEW) return [];
  const { data, error } = await supabase
    .from("jobs")
    .select("*, bookings(id, starts_at, ends_at, status), job_documents(id, superseded_by)")
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  return data as JobSummary[];
}

export async function getJob(id: string): Promise<JobDetail | null> {
  if (PREVIEW) return previewOnly();
  const { data, error } = await supabase
    .from("jobs")
    .select("*, bookings(id, status, shift_type, starts_at, ends_at, crew_size, confirm_override_reason, complete_override_reason, allocations(id, crew_member_id, role, response, decline_reason)), job_documents(*, uploader:profiles!job_documents_uploaded_by_fkey(full_name))")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const job = data as JobDetail;
  job.bookings = [...job.bookings].sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  job.job_documents = [...job.job_documents].sort((a, b) => a.created_at.localeCompare(b.created_at));
  return job;
}

export async function createJob(fields: Partial<JobFields> & { title: string }): Promise<string> {
  if (PREVIEW) return previewOnly();
  const { data, error } = await supabase
    .from("jobs")
    .insert({ ...fields, organisation_id: await organisationId() })
    .select("id")
    .single();
  if (error) throw error;
  return data.id as string;
}

export async function updateJob(id: string, fields: Partial<JobFields>): Promise<void> {
  if (PREVIEW) return previewOnly();
  const { error } = await supabase.from("jobs").update(fields).eq("id", id);
  if (error) throw error;
  // Linking an already signed quote confirms the job's waiting shifts.
  if (fields.quote_id && (await canConfirm(fields.quote_id))) {
    const { error: e2 } = await supabase.from("bookings").update({ status: "confirmed" }).eq("job_id", id).eq("status", "requested");
    if (e2) throw e2;
  }
}

/* ---------- Shifts ---------- */

export interface ShiftDraft {
  date: string; // YYYY-MM-DD
  start: string; // HH:MM
  end: string; // HH:MM; earlier than start means the next day
  crewSize: number;
}

/** Shift start and end instants (an end at or before the start finishes the next day). */
export function shiftTimes(d: ShiftDraft): { startsAt: string; endsAt: string } {
  const start = localInstant(d.date, d.start);
  let end = localInstant(d.date, d.end);
  if (end <= start) end = new Date(end.getTime() + 86400_000);
  return { startsAt: start.toISOString(), endsAt: end.toISOString() };
}

/** Add shifts to a job. Each is a booking linked to the job, carrying the job's address and client. */
export async function addShifts(job: Pick<JobDetail, "id" | "site_address" | "client_name" | "site_contact_name" | "site_contact_phone" | "quote_id">, drafts: ShiftDraft[]): Promise<void> {
  if (PREVIEW) return previewOnly();
  if (!drafts.length) return;
  const org = await organisationId();
  // Shifts start confirmed only when the job's quote is signed (or signing isn't in use yet).
  const status = (await canConfirm(job.quote_id ?? null)) ? "confirmed" : "requested";
  const rows = drafts.map((d) => {
    const { startsAt, endsAt } = shiftTimes(d);
    return {
      organisation_id: org,
      job_id: job.id,
      status,
      shift_type: shiftTypeFor(d.date, d.start),
      starts_at: startsAt,
      ends_at: endsAt,
      crew_size: d.crewSize,
      site_address: job.site_address,
      company: job.client_name,
      contact_name: job.site_contact_name,
      contact_phone: job.site_contact_phone,
      requested_via: "portal",
    };
  });
  const { error } = await supabase.from("bookings").insert(rows);
  if (error) throw error;
}

export async function updateShift(id: string, d: ShiftDraft): Promise<void> {
  if (PREVIEW) return previewOnly();
  const { startsAt, endsAt } = shiftTimes(d);
  const { error } = await supabase.from("bookings").update({ starts_at: startsAt, ends_at: endsAt, crew_size: d.crewSize, shift_type: shiftTypeFor(d.date, d.start) }).eq("id", id);
  if (error) throw error;
}

export async function setShiftStatus(id: string, status: "confirmed" | "allocated" | "completed" | "cancelled"): Promise<void> {
  if (PREVIEW) return previewOnly();
  const extra = status === "cancelled" ? { cancelled_at: new Date().toISOString() } : { cancelled_at: null };
  const { error } = await supabase.from("bookings").update({ status, ...extra }).eq("id", id);
  if (error) throw error;
}

/* ---------- Allocations ---------- */

/** Put someone on a shift. `insuranceOverride` is the recorded reason when they have no current insurance. */
export async function allocate(bookingId: string, crewMemberId: string, role: string, insuranceOverride?: string): Promise<void> {
  if (PREVIEW) return previewOnly();
  const { data: s } = await supabase.auth.getSession();
  const override = insuranceOverride ? { insurance_override_reason: insuranceOverride, insurance_override_by: s.session?.user.id ?? null, insurance_override_at: new Date().toISOString() } : {};
  const { error } = await supabase.from("allocations").insert({ organisation_id: await organisationId(), booking_id: bookingId, crew_member_id: crewMemberId, role, ...override });
  if (error) throw error;
}

/** Put the same person on several shifts (skips shifts they are already on). */
export async function allocateMany(bookingIds: string[], crewMemberId: string, role: string): Promise<void> {
  if (PREVIEW) return previewOnly();
  if (!bookingIds.length) return;
  const org = await organisationId();
  const { error } = await supabase
    .from("allocations")
    .upsert(bookingIds.map((b) => ({ organisation_id: org, booking_id: b, crew_member_id: crewMemberId, role })), { onConflict: "booking_id,crew_member_id", ignoreDuplicates: true });
  if (error) throw error;
}

export async function setAllocationRole(id: string, role: string): Promise<void> {
  if (PREVIEW) return previewOnly();
  const { error } = await supabase.from("allocations").update({ role }).eq("id", id);
  if (error) throw error;
}

export async function unallocate(id: string): Promise<void> {
  if (PREVIEW) return previewOnly();
  const { error } = await supabase.from("allocations").delete().eq("id", id);
  if (error) throw error;
}

/* ---------- Documents ---------- */

export interface UploadInput {
  file: File;
  kind: string;
  title: string;
  versionLabel?: string;
  requiresAck: boolean;
  replaces?: string;
}

export async function uploadJobDocument(jobId: string, u: UploadInput): Promise<void> {
  if (PREVIEW) return previewOnly();
  const form = new FormData();
  form.set("file", u.file);
  form.set("kind", u.kind);
  form.set("title", u.title);
  if (u.versionLabel) form.set("versionLabel", u.versionLabel);
  form.set("requiresAck", String(u.requiresAck));
  if (u.replaces) form.set("replaces", u.replaces);
  await adminFetch(`/jobs/${jobId}/documents`, { method: "POST", body: form }, { timeoutMs: 120_000 });
}

export async function jobDocumentBlob(jobId: string, documentId: string): Promise<Blob> {
  if (PREVIEW) return previewOnly();
  return adminBlob(`/jobs/${jobId}/documents/${documentId}/file`, {}, { timeoutMs: 60_000 });
}

export async function listAcks(documentIds: string[]): Promise<DocumentAck[]> {
  if (PREVIEW || !documentIds.length) return [];
  const { data, error } = await supabase
    .from("document_acks")
    .select("document_id, crew_member_id, kind, signed_name, acknowledged_at")
    .in("document_id", documentIds);
  if (error) throw error;
  return data as DocumentAck[];
}

/* ---------- Crew records and logins (office) ---------- */

export type CrewFields = Pick<CrewMember, "full_name" | "phone" | "email" | "active" | "tickets_held" | "driver_licence_class" | "last_worked_month" | "notes">;

export async function saveCrewMember(id: string | null, fields: CrewFields): Promise<void> {
  if (PREVIEW) return previewOnly();
  const clean = { ...fields, email: fields.email?.trim().toLowerCase() || null, phone: fields.phone?.trim() || null };
  const { error } = id
    ? await supabase.from("crew_members").update(clean).eq("id", id)
    : await supabase.from("crew_members").insert({ ...clean, organisation_id: await organisationId() });
  if (error) throw error;
}

export async function giveCrewLogin(id: string): Promise<{ created: boolean; emailed: boolean }> {
  if (PREVIEW) return previewOnly();
  return adminFetch(`/crew/${id}/login`, { method: "POST", body: JSON.stringify({ sendEmail: true }) });
}

export async function crewSignInLink(id: string): Promise<string> {
  if (PREVIEW) return previewOnly();
  return (await adminFetch<{ url: string }>(`/crew/${id}/signin-link`, { method: "POST" })).url;
}

/* ---------- Crew side (job packs) ---------- */

export async function myShifts(): Promise<{ crewMember: { id: string; name: string } | null; shifts: MyShift[] }> {
  if (PREVIEW) return { crewMember: null, shifts: [] };
  return crewFetch("/shifts");
}

export async function jobPack(bookingId: string): Promise<JobPack> {
  if (PREVIEW) return previewOnly();
  return crewFetch(`/shifts/${bookingId}`);
}

export async function packDocumentBlob(bookingId: string, documentId: string): Promise<Blob> {
  if (PREVIEW) return previewOnly();
  return crewBlob(`/shifts/${bookingId}/documents/${documentId}`, { timeoutMs: 60_000 });
}

export async function acknowledge(bookingId: string, documentId: string, signedName?: string): Promise<void> {
  if (PREVIEW) return previewOnly();
  await crewFetch(`/shifts/${bookingId}/documents/${documentId}/ack`, { method: "POST", body: JSON.stringify(signedName ? { signedName } : {}) });
}

/**
 * Open a downloaded file in a new tab. The tab is opened before the download starts so phone
 * browsers don't block it as a pop-up; if they do, the file opens in this tab instead.
 */
export async function openBlobInNewTab(load: () => Promise<Blob>): Promise<void> {
  const tab = window.open("", "_blank");
  try {
    const blob = await load();
    const url = URL.createObjectURL(blob);
    if (tab) tab.location.href = url;
    else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
  } catch (e) {
    tab?.close();
    throw e;
  }
}
